import { PathHelper } from '@sensenet/client-utils';

/**
 * Class that contains shortcuts for security-related custom content actions
 */
export class Security {
  constructor(repository) {
    this.repository = repository;

    this.setPermissions = (idOrPath, permissionRequestBody) => this.repository.executeAction({
      name: 'SetPermissions',
      idOrPath,
      method: 'POST',
      body: {
        r: permissionRequestBody
      }
    });

    this.getAllPermissions = contentIdOrPath => this.repository.executeAction({
      idOrPath: contentIdOrPath,
      name: 'GetPermissions',
      method: 'GET',
      body: undefined
    });

    this.getAcl = contentIdOrPath => this.repository.executeAction({
      idOrPath: contentIdOrPath,
      name: 'GetAcl',
      method: 'GET'
    });

    this.getPermissionsForIdentity = (contentIdOrPath, identityPath) => this.repository.executeAction({
      idOrPath: contentIdOrPath,
      name: 'GetPermissions',
      method: 'GET',
      body: {
        identity: identityPath
      }
    });

    this.getRelatedIdentities = options => this.repository.executeAction({
      name: 'GetRelatedIdentities',
      idOrPath: options.contentIdOrPath,
      method: 'POST',
      body: {
        permissionLevel: options.level,
        indentityKind: options.kind
      },
      oDataOptions: options.oDataOptions
    });

    this.getRelatedPermissions = options => {
      var _options$includedType;

      return this.repository.executeAction({
        name: 'GetRelatedPermissions',
        idOrPath: options.contentIdOrPath,
        method: 'POST',
        body: {
          permissionLevel: options.level,
          explicitOnly: options.explicitOnly,
          memberPath: options.memberPath,
          includedTypes: (_options$includedType = options.includedTypes) !== null && _options$includedType !== void 0 ? _options$includedType : null
        },
        oDataOptions: options.oDataOptions
      });
    };

    this.getRelatedItems = options => this.repository.executeAction({
      name: 'GetRelatedItems',
      idOrPath: options.contentIdOrPath,
      method: 'POST',
      body: {
        permissionLevel: options.level,
        explicitOnly: options.explicitOnly,
        memberPath: options.member,
        permissions: options.permissions
      },
      oDataOptions: options.oDataOptions
    });

    this.getRelatedIdentitiesByPermissions = options => this.repository.executeAction({
      name: 'GetRelatedIdentitiesByPermissions',
      idOrPath: options.contentIdOrPath,
      method: 'POST',
      body: {
        permissionLevel: options.level,
        identityKind: options.kind,
        permissions: options.permissions
      },
      oDataOptions: options.oDataOptions
    });

    this.getRelatedItemsOneLevel = options => this.repository.executeAction({
      name: 'GetRelatedItemsOneLevel',
      idOrPath: options.contentIdOrPath,
      method: 'POST',
      body: {
        permissionLevel: options.level,
        memberPath: options.member,
        permissions: options.permissions
      },
      oDataOptions: options.oDataOptions
    });

    this.getAllowedUsers = options => this.repository.executeAction({
      idOrPath: options.contentIdOrPath,
      name: 'GetAllowedUsers',
      method: 'POST',
      body: {
        permissions: options.permissions
      },
      oDataOptions: options.oDataOptions
    });

    this.getParentGroups = options => this.repository.executeAction({
      name: 'GetParentGroups',
      idOrPath: options.contentIdOrPath,
      method: 'POST',
      body: {
        directOnly: options.directOnly
      },
      oDataOptions: options.oDataOptions,
      requestInit: options.requestInit
    });

    this.addMembers = (contentIdOrPath, contentIds) => this.repository.executeAction({
      name: 'AddMembers',
      idOrPath: contentIdOrPath,
      method: 'POST',
      body: {
        contentIds
      }
    });

    this.removeMembers = (contentIdOrPath, contentIds) => this.repository.executeAction({
      name: 'RemoveMembers',
      idOrPath: contentIdOrPath,
      method: 'POST',
      body: {
        contentIds
      }
    });
  }
  /**
   * Sets permissions on the requested content.
   * You can add or remove permissions for one ore more users or groups using this action.
   * @param {string | number} idOrPath A content id or path
   * @param {PermissionRequestBody} permissionRequestBody inheritance: break or unbreak
   * @returns {Promise<PermissionResponseModel>} A promise with a response model
   */


  /**
   * Gets if the given user has the specified permissions for the requested content.
   *
   * Required permissions to call this action: See permissions.
   * @param {string[]} permissions list of permission names (e.g. Open, Save)
   * @param {string} user path of the user (or the current user, if not provided)
   * @returns {Promise<boolean>} A promise with the response value
   */
  async hasPermission(contentIdOrPath, permissions, identityPath, requestInit) {
    let params = `permissions=${permissions.join(',')}`;

    if (identityPath) {
      params += `&identity=${identityPath}`;
    }

    const path = PathHelper.joinPaths(this.repository.configuration.repositoryUrl, this.repository.configuration.oDataToken, PathHelper.getContentUrl(contentIdOrPath));
    const response = await this.repository.fetch(`${path}/HasPermission?${params}`, requestInit);

    if (response.ok) {
      return (await response.text()) === 'true' || false;
    } else {
      throw await this.repository.getErrorFromResponse(response);
    }
  }
  /**
   * Identity list that contains every users/groups/organizational units
   * that have any permission setting (according to permission level)
   * in the subtree of the context content.
   * @param {GetRelatedIdentities<TIdentityType>} options Options object for the method call
   * @returns {Promise<ODataCollectionResponse<TIdentityType>>} A promise that will be resolved with a collection of related identities
   */


}
//# sourceMappingURL=Security.js.map